import { useParams, useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { DetailPage, space } from '@viq-admin/ui';
import { adminFetch } from '../../api/dataProvider';
import { ConferencesPanel } from './ConferencesPanel';
import { ActivitySummary } from './ActivitySummary';
import { UserActions } from './UserActions';
import { repDisplayName } from '../../labels/repName';
import { displayName } from '../../labels/display';

/**
 * Shape returned by GET /v1/admin/users/:oid.
 *
 * The list endpoint returns UserProfile (camelCase), so we expect the
 * same shape here. `conferences` may be present if the backend
 * includes it; fall back to an empty array if absent.
 */
interface UserDetailResponse {
  oid: string;
  email: string;
  name: string;
  companyId: string;
  companyName?: string;
  role: string;
  lastLoginAt: string | null;
  createdAt: string;
  updatedAt: string;
  conferenceIds: string[];
  deactivatedAt: string | null;
  /** Optional rich conferences array; included if backend serializes it. */
  conferences?: Array<{ id: string; name: string }>;
}

/**
 * Portal user detail on the shared read-only DetailPage shell: header (name, a
 * role·company·email metadata line, breadcrumb + back), then the deactivate
 * actions, the conferences panel, and the activity summary. No Edit affordance
 * — the portal has no user-edit endpoint — so it passes no editView.
 */
export function UserDetailView() {
  const { oid } = useParams<{ oid: string }>();
  const navigate = useNavigate();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['user', oid],
    queryFn: () => adminFetch<UserDetailResponse>(`/v1/admin/users/${oid}`),
    enabled: !!oid,
  });

  const inactive = !!data?.deactivatedAt;
  const fullName = data ? data.name || data.email : '';

  // Build conference entries from the rich array when available;
  // conference IDs-only arrays (no names) render as the ID string.
  const conferences: Array<{ id: string; name: string }> = data
    ? data.conferences ??
      (data.conferenceIds ?? []).map((id) => ({ id, name: id }))
    : [];

  return (
    <DetailPage
      title={data ? repDisplayName(fullName, inactive) : 'User'}
      subtitle={
        data ? (
          <>
            {displayName('role', data.role)} · {data.companyName ?? data.companyId}{' '}
            · {data.email}
          </>
        ) : undefined
      }
      breadcrumbs={[{ label: 'Users', href: '/users' }, { label: fullName || 'User' }]}
      onBack={() => navigate('/users')}
      onNavigate={(t) => navigate(t.href)}
      loading={isLoading}
      // adminFetch rejects on any non-2xx; the record either resolves or it
      // does not, so an error surfaces as not-found (matching the prior bare
      // <Result 404>), with a Back-to-list affordance.
      notFound={!isLoading && (!!error || !data)}
      notFoundHref="/users"
      readView={
        data ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: space.xl }}>
            <UserActions user={data} onChanged={() => refetch()} />
            <ConferencesPanel
              oid={oid!}
              userCompanyId={data.companyId}
              conferences={conferences}
              onChanged={() => refetch()}
            />
            <ActivitySummary oid={oid!} />
          </div>
        ) : null
      }
    />
  );
}
